import { type ReactNode, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { LogOut, Mail, Sun, Moon, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslations } from '@/i18n/useTranslations';
import { useTheme } from '@/components/theme-provider';
import { useNavigation } from '@/hooks/use-navigation';
import { useLogout } from '@/hooks/use-logout';
import { useAuthStore } from '@/stores/auth-store';
import { useMessageStore } from '@/stores/message-store';
import { useUiModeStore } from '@/stores/ui-mode-store';
import { useAiChatStore } from '@/stores/ai-chat-store';
import { useShowTryGuidedButton } from '@/hooks/use-guided-ui-mode';
import { useFooterConfig } from '@/hooks/use-footer-config';
import { UserRole } from '@comp-sys/shared';
import { stripLocale } from '@/lib/navigation-config';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { LanguageSwitcher } from '@/components/ui/language-switcher';
import { MobileSidebar } from '@/components/layout/mobile-sidebar';
import { BrandLogo } from '@/components/shared/brand-logo';
import { ConfigurableFooter } from '@/components/shared/configurable-footer';
import { ProfileReviewAlert } from '@/components/shared/profile-review-alert';
import { AiChatPanel } from '@/components/ai/ai-chat-panel';
import { AiFloatingButton } from '@/components/ai/ai-floating-button';

/** 朱砂印色 —— ink 家族的文化装饰强调色（固定语义，非主题 token）。 */
const CINNABAR = '#9b2c2c';

/**
 * Ink 家族 shell（ink-stone / xuan 主题）——「书卷」。
 *
 * 与其他家族截然不同的「阅读」范式：
 * - 左侧细窄「卷轴目录」：竖排（writing-mode: vertical-rl）书法导航 + 朱砂印章式激活标记
 * - 内容居中成「页」：大留白、衬线大标题、卷首页眉（朱砂方框章节标），如展卷阅读
 * - 翻页式过渡（淡入 + 横移 + 轻虚化），而非工具式滑动
 * - 顶栏极薄：居中衬线索面包屑 + 朱砂发丝线，无重 chrome
 *
 * 子导航：当前激活的顶级项若有子项，在卷首页眉下横向铺开为「目录」子链。
 */
export function InkShell({ children }: { children: ReactNode }) {
  const location = useLocation();
  const pathname = location.pathname;
  const user = useAuthStore((state) => state.user);
  const { navItems } = useNavigation();
  const unreadCount = useMessageStore((s) => s.unreadCount);
  const { config: footerConfig } = useFooterConfig();
  const aiIsOpen = useAiChatStore((s) => s.isOpen);
  const aiIsFloating = useAiChatStore((s) => s.isFloating);
  const isSystemAdmin = user?.activeRole === 'SYSTEM_ADMIN';
  const t = useTranslations('nav');

  const prefersReducedMotion = useMemo(
    () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    [],
  );

  const isActive = (href: string) =>
    pathname === href || (href !== '/dashboard' && pathname.startsWith(href + '/'));

  // 当前激活顶级项的子项 → 渲染为「目录」子导航
  const activeTop = navItems.find((i) => isActive(i.href) && i.children?.length);
  const subNav = activeTop?.children ?? [];

  return (
    <div className="min-h-screen">
      {/* 左侧卷轴目录 */}
      <aside
        className={cn(
          'fixed left-0 top-0 z-40 h-screen w-20 flex flex-col items-center font-serif',
          'bg-background/95 backdrop-blur-sm border-r border-border',
          'hidden md:flex',
        )}
      >
        <div className="h-16 flex items-center justify-center border-b border-border w-full">
          <BrandLogo variant="auto" size="sm" collapsed linkTo="/" />
        </div>

        <nav className="flex-1 overflow-auto py-3 flex flex-col items-center gap-1">
          {navItems.map((item) => {
            const active = isActive(item.href);
            const Icon = item.icon;
            return (
              <Link
                key={item.href}
                to={item.href}
                title={t(item.labelKey)}
                className={cn(
                  'relative flex flex-col items-center gap-1 w-16 py-2 rounded transition-colors',
                  active ? 'text-foreground' : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {active && (
                  <span
                    className="absolute -right-0 top-2 bottom-2 w-0.5 rounded"
                    style={{ backgroundColor: CINNABAR }}
                  />
                )}
                {active && (
                  <span
                    className="absolute left-1.5 top-2 h-2 w-2 rotate-45"
                    style={{ backgroundColor: CINNABAR }}
                    aria-hidden
                  />
                )}
                <Icon className="h-4 w-4" />
                <span
                  className="text-[12px] leading-tight tracking-wider"
                  style={{ writingMode: 'vertical-rl' }}
                >
                  {t(item.labelKey)}
                </span>
              </Link>
            );
          })}
        </nav>

        <InkUserMenu />
      </aside>

      {/* 主区（居中成卷） */}
      <div className="md:ml-20 flex items-start min-h-screen">
        <div className="flex-1 min-w-0 flex flex-col min-h-screen">
          <InkTopBar pathname={pathname} unreadCount={unreadCount} />

          <main id="main-content" className="flex-1 px-4 py-6 md:py-10">
            <div className="mx-auto max-w-4xl">
              <ProfileReviewAlert />

              {/* 卷首页眉：朱砂方框章节标 */}
              <div className="flex items-center gap-3 mb-6">
                <span
                  className="inline-flex items-center justify-center h-7 px-2 font-serif text-[12px] tracking-[0.3em]"
                  style={{ border: `1px solid ${CINNABAR}`, color: CINNABAR }}
                >
                  卷
                </span>
                <span className="h-px flex-1" style={{ backgroundColor: CINNABAR, opacity: 0.25 }} />
              </div>

              {/* 目录子导航 */}
              {subNav.length > 0 && (
                <nav className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-6 font-serif text-sm">
                  {subNav.map((child, i) => {
                    const childActive = isActive(child.href);
                    return (
                      <span key={child.href} className="flex items-center gap-x-4">
                        {i > 0 && <span className="text-muted-foreground/40">·</span>}
                        <Link
                          to={child.href}
                          className={cn(
                            'transition-colors',
                            childActive ? 'font-semibold' : 'text-muted-foreground hover:text-foreground',
                          )}
                          style={childActive ? { color: CINNABAR } : undefined}
                        >
                          {t(child.labelKey)}
                        </Link>
                      </span>
                    );
                  })}
                </nav>
              )}

              <AnimatePresence>
                <motion.div
                  key={pathname}
                  initial={prefersReducedMotion ? false : { opacity: 0, x: -8, filter: 'blur(4px)' }}
                  animate={{ opacity: 1, x: 0, filter: 'blur(0px)' }}
                  exit={prefersReducedMotion ? undefined : { opacity: 0, position: 'absolute' }}
                  transition={{ duration: 0.35, ease: 'easeOut' }}
                >
                  {children}
                </motion.div>
              </AnimatePresence>
            </div>
          </main>

          <ConfigurableFooter variant="dashboard" config={footerConfig} />
        </div>

        {isSystemAdmin && aiIsOpen && !aiIsFloating && <AiChatPanel mode="fixed" />}
      </div>
      {isSystemAdmin && aiIsFloating && <AiChatPanel mode="floating" />}
      {isSystemAdmin && !aiIsOpen && <AiFloatingButton />}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* 卷首顶栏：极薄，居中衬线索面包屑 + 朱砂发丝线                        */
/* ------------------------------------------------------------------ */

function InkTopBar({ pathname, unreadCount }: { pathname: string; unreadCount: number }) {
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const tCommon = useTranslations('common');
  // 引导式回切:资格角色在经典模式下可见,点此回到引导式首页。
  const setUiMode = useUiModeStore((s) => s.setUiMode);
  const guidedEligible = useShowTryGuidedButton();
  const tGuided = useTranslations('guided');
  const path = stripLocale(pathname);

  return (
    <header className="sticky top-0 z-30 bg-background/90 backdrop-blur-sm">
      <div className="mx-auto max-w-4xl flex h-14 items-center gap-3 px-4">
        <div className="md:hidden">
          <MobileSidebar />
        </div>

        <span className="font-serif text-sm text-muted-foreground truncate flex-1 text-center">
          {path || '/'}
        </span>

        <div className="flex items-center gap-1">
          {guidedEligible && (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 font-serif"
              onClick={() => setUiMode('guided')}
              aria-label={tGuided('switchGuided')}
              title={tGuided('switchGuided')}
            >
              <Sparkles className="h-4 w-4" />
              <span className="ml-1 hidden lg:inline">{tGuided('switchGuided')}</span>
            </Button>
          )}
          <LanguageSwitcher />
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
            aria-label={tCommon('toggleTheme')}
          >
            <Sun className="h-4 w-4 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
            <Moon className="absolute h-4 w-4 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="relative h-8 w-8"
            onClick={() => navigate('/dashboard/messages')}
            aria-label={tCommon('openMessages')}
          >
            <Mail className="h-4 w-4" />
            {unreadCount > 0 && (
              <span
                className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 flex items-center justify-center text-[9px] font-bold text-white px-1 rounded-full"
                style={{ backgroundColor: CINNABAR }}
              >
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </Button>
        </div>
      </div>
      <div className="h-px w-full" style={{ backgroundColor: CINNABAR, opacity: 0.2 }} />
    </header>
  );
}

/* ------------------------------------------------------------------ */
/* 用户入口（极简）                                                    */
/* ------------------------------------------------------------------ */

function InkUserMenu() {
  const user = useAuthStore((state) => state.user);
  const { logout } = useLogout();
  const [open, setOpen] = useState(false);
  const tAuth = useTranslations('auth');
  const isPrivileged =
    user?.activeRole === UserRole.SYSTEM_ADMIN || user?.activeRole === UserRole.SUPER_ADMIN;

  // 极简：点击头像直接展开内联退出（避免下拉菜单破坏书卷氛围）
  return (
    <div className="border-t border-border w-full py-3 flex flex-col items-center gap-2">
      <Avatar className="h-8 w-8 rounded-full border border-border">
        <AvatarImage src="/avatar.png" />
        <AvatarFallback className="text-[11px]">
          {user?.displayName?.[0]?.toUpperCase() || 'U'}
        </AvatarFallback>
      </Avatar>
      <button
        onClick={() => setOpen((v) => !v)}
        className="text-[10px] text-muted-foreground hover:text-foreground font-serif"
        title={user?.displayName || user?.email || ''}
      >
        {open ? '收' : user?.displayName?.slice(0, 2) || '我'}
      </button>
      {open && (
        <div className="flex flex-col items-center gap-1 pb-1">
          {isPrivileged && (
            <Link
              to="/dashboard/settings"
              className="text-[10px] text-muted-foreground hover:text-foreground font-serif"
            >
              设置
            </Link>
          )}
          <button
            onClick={logout}
            className="flex items-center gap-1 text-[10px] hover:text-foreground font-serif"
            style={{ color: CINNABAR }}
          >
            <LogOut className="h-3 w-3" />
            {tAuth('logout.title')}
          </button>
        </div>
      )}
    </div>
  );
}
